import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Info, ChevronDown, ChevronUp, ExternalLink, BookOpen } from "lucide-react";
import NakshatraWheelChart, { ChartLegend } from "./NakshatraWheelChart";
import { NorthIndianChart, NatalPlanetPopup, HousePopup, HOUSE_DATA, getPlanetInfo } from "./northIndianChart";

// Zodiac signs data
const SIGNS = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"
];

const SIGN_SYMBOLS = {
  Aries: "♈", Taurus: "♉", Gemini: "♊", Cancer: "♋",
  Leo: "♌", Virgo: "♍", Libra: "♎", Scorpio: "♏",
  Sagittarius: "♐", Capricorn: "♑", Aquarius: "♒", Pisces: "♓"
};

const SIGN_COLORS = {
  Aries: "#EF4444", Taurus: "#22C55E", Gemini: "#F59E0B", Cancer: "#94A3B8",
  Leo: "#F97316", Virgo: "#10B981", Libra: "#EC4899", Scorpio: "#8B5CF6",
  Sagittarius: "#F59E0B", Capricorn: "#6B7280", Aquarius: "#3B82F6", Pisces: "#06B6D4"
};

// Links to Learn section articles
const LEARN_LINKS = {
  // Planet articles
  planets: {
    Sun: { slug: "sun-surya", title: "Sun (Surya)" },
    Moon: { slug: "moon-chandra", title: "Moon (Chandra)" },
    Mars: { slug: "mars-mangal", title: "Mars (Mangal)" },
    Mercury: { slug: "mercury-budha", title: "Mercury (Budha)" },
    Jupiter: { slug: "jupiter-guru", title: "Jupiter (Guru)" },
    Venus: { slug: "venus-shukra", title: "Venus (Shukra)" },
    Saturn: { slug: "saturn-shani", title: "Saturn (Shani)" },
    Rahu: { slug: "rahu-north-node", title: "Rahu (North Node)" },
    Ketu: { slug: "ketu-south-node", title: "Ketu (South Node)" },
  },
  // Sign articles
  signs: {
    Aries: { slug: "aries-mesha", title: "Aries (Mesha)" },
    Taurus: { slug: "taurus-vrishabha", title: "Taurus (Vrishabha)" },
    Gemini: { slug: "gemini-mithuna", title: "Gemini (Mithuna)" },
    Cancer: { slug: "cancer-karka", title: "Cancer (Karka)" },
    Leo: { slug: "leo-simha", title: "Leo (Simha)" },
    Virgo: { slug: "virgo-kanya", title: "Virgo (Kanya)" },
    Libra: { slug: "libra-tula", title: "Libra (Tula)" },
    Scorpio: { slug: "scorpio-vrishchika", title: "Scorpio (Vrishchika)" },
    Sagittarius: { slug: "sagittarius-dhanu", title: "Sagittarius (Dhanu)" },
    Capricorn: { slug: "capricorn-makara", title: "Capricorn (Makara)" },
    Aquarius: { slug: "aquarius-kumbha", title: "Aquarius (Kumbha)" },
    Pisces: { slug: "pisces-meena", title: "Pisces (Meena)" },
  },
};

// Planet display data
const PLANET_DATA = {
  Sun: { glyph: "☉", color: "#F97316", order: 1 },
  Moon: { glyph: "☽", color: "#94A3B8", order: 2 },
  Mars: { glyph: "♂", color: "#EF4444", order: 3 },
  Mercury: { glyph: "☿", color: "#22C55E", order: 4 },
  Jupiter: { glyph: "♃", color: "#F59E0B", order: 5 },
  Venus: { glyph: "♀", color: "#EC4899", order: 6 },
  Saturn: { glyph: "♄", color: "#6B7280", order: 7 },
  Rahu: { glyph: "☊", color: "#8B5CF6", order: 8 },
  Ketu: { glyph: "☋", color: "#06B6D4", order: 9 },
};

// Nakshatra list for lookups
const NAKSHATRA_LIST = [
  "Ashwini", "Bharani", "Krittika", "Rohini", "Mrigashira", "Ardra",
  "Punarvasu", "Pushya", "Ashlesha", "Magha", "Purva Phalguni", "Uttara Phalguni",
  "Hasta", "Chitra", "Swati", "Vishakha", "Anuradha", "Jyeshtha",
  "Mula", "Purva Ashadha", "Uttara Ashadha", "Shravana", "Dhanishta", "Shatabhisha",
  "Purva Bhadrapada", "Uttara Bhadrapada", "Revati"
];

// Convert nakshatra name to URL slug
function nakshatraToSlug(name) {
  return name.toLowerCase().replace(/\s+/g, '-');
}

// Planet Row Component with Learn links
function PlanetRow({ planet, data, isExpanded, onToggle }) {
  const planetInfo = PLANET_DATA[planet];
  const planetLearn = LEARN_LINKS.planets[planet];
  const signLearn = LEARN_LINKS.signs[data.tropical_sign];
  
  return (
    <div className="rounded-lg overflow-hidden" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
      {/* Planet Header - Clickable */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
        data-testid={`planet-row-${planet.toLowerCase()}`}
      >
        <div className="flex items-center gap-3">
          {/* Planet glyph */}
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
            style={{ background: `${planetInfo.color}15`, color: planetInfo.color, border: `1px solid ${planetInfo.color}30` }}
          >
            {planetInfo.glyph}
          </div>
          
          {/* Planet name and position */}
          <div className="text-left">
            <p className="text-white/90 text-sm font-medium">{planet}</p>
            <p className="text-white/50 text-xs">
              {SIGN_SYMBOLS[data.tropical_sign]} {data.tropical_sign} {data.tropical_degree?.toFixed(1)}°
            </p>
          </div>
        </div>
        
        {/* Nakshatra badge */}
        <div className="flex items-center gap-2">
          <span className="text-amber-500/70 text-xs bg-amber-500/10 px-2 py-0.5 rounded">
            {data.nakshatra}
          </span>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-white/30" />
          ) : (
            <ChevronDown className="w-4 h-4 text-white/30" />
          )}
        </div>
      </button>
      
      {/* Expanded Details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 space-y-3">
              {/* Tropical Sign */}
              <div className="flex items-center justify-between p-2 rounded" style={{ background: `${SIGN_COLORS[data.tropical_sign]}10` }}>
                <div>
                  <p className="text-white/40 text-[10px] tracking-[0.1em] uppercase">Tropical Sign</p>
                  <p className="text-white/80 text-sm">
                    {SIGN_SYMBOLS[data.tropical_sign]} {data.tropical_sign} at {data.tropical_degree?.toFixed(2)}°
                  </p>
                </div>
                {signLearn && (
                  <Link
                    to={`/learn/signs/${signLearn.slug}`}
                    className="flex items-center gap-1 text-xs text-white/50 hover:text-white/80 transition-colors"
                    data-testid={`learn-sign-${data.tropical_sign.toLowerCase()}`}
                  >
                    <BookOpen className="w-3 h-3" />
                    Learn
                  </Link>
                )}
              </div>
              
              {/* Sidereal Nakshatra */}
              <div className="flex items-center justify-between p-2 rounded" style={{ background: "rgba(251,191,36,0.05)" }}>
                <div>
                  <p className="text-white/40 text-[10px] tracking-[0.1em] uppercase">Sidereal Nakshatra</p>
                  <p className="text-white/80 text-sm">
                    {data.nakshatra} · Pada {data.nakshatra_pada}
                  </p>
                  <p className="text-white/40 text-xs">Ruler: {data.nakshatra_ruler}</p>
                </div>
                <Link
                  to={`/nakshatras/${nakshatraToSlug(data.nakshatra)}`}
                  className="flex items-center gap-1 text-xs text-amber-500/70 hover:text-amber-400 transition-colors"
                  data-testid={`learn-nakshatra-${nakshatraToSlug(data.nakshatra)}`}
                >
                  <BookOpen className="w-3 h-3" />
                  Learn
                </Link>
              </div>
              
              {/* Learn about this planet */}
              {planetLearn && (
                <Link
                  to={`/learn/planets/${planetLearn.slug}`}
                  className="flex items-center justify-center gap-2 w-full py-2 rounded text-xs text-white/50 hover:text-white/80 hover:bg-white/5 transition-colors"
                  data-testid={`learn-planet-${planet.toLowerCase()}`}
                >
                  <BookOpen className="w-3.5 h-3.5" />
                  Learn about {planet} in Vedic Astrology
                  <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Ascendant Info Component
function AscendantInfo({ ascendant }) {
  if (!ascendant) return null;
  
  const signLearn = LEARN_LINKS.signs[ascendant.ascendant_sign];
  
  return (
    <div className="rounded-lg p-4" style={{ background: "rgba(251,191,36,0.05)", border: "1px solid rgba(251,191,36,0.15)" }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/40 text-[10px] tracking-[0.1em] uppercase mb-1">Ascendant (Lagna)</p>
          <p className="text-white/90 text-lg font-light">
            {SIGN_SYMBOLS[ascendant.ascendant_sign]} {ascendant.ascendant_sign}
          </p>
          <p className="text-white/50 text-xs">
            {ascendant.ascendant_degree?.toFixed(1)}° · Rising Nakshatra: {ascendant.rising_nakshatra}
          </p>
        </div>
        <div className="flex flex-col gap-1">
          {signLearn && (
            <Link
              to={`/learn/signs/${signLearn.slug}`}
              className="flex items-center gap-1 text-xs text-amber-500/70 hover:text-amber-400 transition-colors"
            >
              <BookOpen className="w-3 h-3" />
              Learn about {ascendant.ascendant_sign}
            </Link>
          )}
          <Link
            to={`/nakshatras/${nakshatraToSlug(ascendant.rising_nakshatra)}`}
            className="flex items-center gap-1 text-xs text-white/50 hover:text-white/80 transition-colors"
          >
            <BookOpen className="w-3 h-3" />
            {ascendant.rising_nakshatra} Nakshatra
          </Link>
        </div>
      </div>
    </div>
  );
}

// Quick Learn Links Component
function QuickLearnLinks() {
  return (
    <div className="rounded-lg p-4" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
      <p className="text-white/40 text-[10px] tracking-[0.15em] uppercase mb-3">Deepen Your Understanding</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Link
          to="/learn/foundations"
          className="flex items-center gap-2 p-2 rounded text-xs text-white/60 hover:text-white/90 hover:bg-white/5 transition-colors"
        >
          <BookOpen className="w-3.5 h-3.5 text-amber-500/70" />
          Foundations
        </Link>
        <Link
          to="/learn/planets"
          className="flex items-center gap-2 p-2 rounded text-xs text-white/60 hover:text-white/90 hover:bg-white/5 transition-colors"
        >
          <BookOpen className="w-3.5 h-3.5 text-amber-500/70" />
          Planets
        </Link>
        <Link
          to="/learn/signs"
          className="flex items-center gap-2 p-2 rounded text-xs text-white/60 hover:text-white/90 hover:bg-white/5 transition-colors"
        >
          <BookOpen className="w-3.5 h-3.5 text-amber-500/70" />
          Signs
        </Link>
        <Link
          to="/learn/nakshatras"
          className="flex items-center gap-2 p-2 rounded text-xs text-white/60 hover:text-white/90 hover:bg-white/5 transition-colors"
        >
          <BookOpen className="w-3.5 h-3.5 text-amber-500/70" />
          Nakshatras
        </Link>
      </div>
    </div>
  );
}

// Main Export: Full Planetary Chart (North Indian natal layout)
export default function FullPlanetaryChart({ planets, ascendant, houses }) {
  const [expandedPlanet, setExpandedPlanet] = useState(null);
  const [selectedPlanet, setSelectedPlanet] = useState(null);
  const [selectedHouse, setSelectedHouse] = useState(null);

  if (!planets || Object.keys(planets).length === 0) {
    return null;
  }

  // Sort planets by traditional order
  const sortedPlanets = Object.entries(planets).sort(([a], [b]) => {
    return (PLANET_DATA[a]?.order || 99) - (PLANET_DATA[b]?.order || 99);
  });

  // Build natal planet objects for the North Indian chart
  const natalPlanets = sortedPlanets.map(([name, d]) => ({
    planet: name,
    glyph: d.glyph || getPlanetInfo(name)?.glyph || "?",
    house: d.house,
    house_sign: d.house_sign || d.tropical_sign,
    tropical_sign: d.tropical_sign,
    tropical_degree: d.tropical_degree,
    nakshatra: d.nakshatra,
    nakshatra_pada: d.nakshatra_pada,
    retrograde: d.retrograde,
  }));

  const handlePlanetClick = (p) => setSelectedPlanet(p);
  const handleHouseClick = (house, sign, symbol, planetsHere) =>
    setSelectedHouse({ house, sign, symbol, planets: planetsHere, data: HOUSE_DATA[house] });

  return (
    <div className="space-y-6" data-testid="full-planetary-chart">
      {/* North Indian Natal Chart */}
      <div className="rounded-2xl p-6 sm:p-8" style={{ background: "linear-gradient(135deg, rgba(12,12,15,0.95) 0%, rgba(6,6,8,0.95) 100%)", border: "1px solid rgba(255,255,255,0.05)", boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-white/40 text-xs tracking-[0.15em] uppercase">Natal Chart (North Indian)</p>
          <span className="text-white/30 text-[10px] bg-white/5 px-2 py-0.5 rounded">
            Birth Placements · Whole-Sign Houses
          </span>
        </div>

        {ascendant && (
          <div className="text-center mb-6">
            <p className="text-amber-400/60 text-sm tracking-wider">
              Ascendant in {ascendant.ascendant_sign} {ascendant.ascendant_symbol}
            </p>
          </div>
        )}

        <NorthIndianChart
          houses={houses}
          activatedPlanets={natalPlanets}
          currentMoon={null}
          onPlanetClick={handlePlanetClick}
          onHouseClick={handleHouseClick}
          selectedPlanetName={selectedPlanet?.planet}
        />

        <div className="mt-6 pt-4 border-t border-white/5">
          <p className="text-center text-white/20 text-xs">Tap a house or planet for birth-placement details • ℞ = retrograde</p>
        </div>
      </div>

      {/* Ascendant */}
      <AscendantInfo ascendant={ascendant} />

      {/* Planet List (synced with chart) */}
      <div className="space-y-2">
        <p className="text-white/40 text-xs tracking-[0.15em] uppercase mb-3">Nine-Planet Natal Placements</p>
        {sortedPlanets.map(([planet, data]) => {
          const isSelected = selectedPlanet?.planet === planet;
          return (
            <div
              key={planet}
              onClick={() => setSelectedPlanet(natalPlanets.find((p) => p.planet === planet))}
              className={`rounded-xl transition-all ${isSelected ? "ring-1 ring-amber-400/50 bg-amber-400/5" : ""}`}
              data-testid={`natal-card-${planet}`}
            >
              <PlanetRow
                planet={planet}
                data={data}
                isExpanded={expandedPlanet === planet}
                onToggle={() => setExpandedPlanet(expandedPlanet === planet ? null : planet)}
              />
            </div>
          );
        })}
      </div>

      {/* Quick Learn Links */}
      <QuickLearnLinks />

      {/* Popups */}
      <AnimatePresence>
        {selectedPlanet && (
          <NatalPlanetPopup
            planet={selectedPlanet}
            planetInfo={getPlanetInfo(selectedPlanet.planet)}
            mode="natal"
            onClose={() => setSelectedPlanet(null)}
          />
        )}
        {selectedHouse && (
          <HousePopup
            houseData={selectedHouse.data}
            sign={selectedHouse.sign}
            symbol={selectedHouse.symbol}
            planets={selectedHouse.planets}
            onClose={() => setSelectedHouse(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Export individual components for flexible usage
export { PlanetRow, AscendantInfo, QuickLearnLinks, LEARN_LINKS };
